import type { UpstartLendingStates } from '../financingTypes';

export enum Constants {
  DEMO_DEALER_ID = 'demo',
  PRODIGY_DEV_DEALER_ID = 'demo-dev-routeone',
  PRODIGY_PROD_DEALER_ID = 'demo-prod-routeone',
  EMAIL_TEST_KEYWORD = 'prodigy',
}

export enum Condition {
  NEW = 'New',
  USED = 'Used',
  CPO = 'CPO',
}

export enum EmploymentStatusEnum {
  ACTIVE_MILITARY = 'Active Military',
  CONTRACT = 'Contract',
  FULL_TIME = 'Full Time',
  PART_TIME = 'Part Time',
  SEASONAL = 'Seasonal',
  SELF_EMPLOYED = 'Self Employed',
  TEMPORARY = 'Temporary',
  RETIRED = 'Retired',
  NOT_APPLICABLE = 'Not Applicable',
}

// Route One selectors for EmploymentStatus
export const EmploymentStatus: {
  EMPLOYED: string[];
  UNEMPLOYED: string[];
} = {
  EMPLOYED: [
    EmploymentStatusEnum.ACTIVE_MILITARY,
    EmploymentStatusEnum.CONTRACT,
    EmploymentStatusEnum.FULL_TIME,
    EmploymentStatusEnum.PART_TIME,
    EmploymentStatusEnum.SEASONAL,
    EmploymentStatusEnum.SELF_EMPLOYED,
    EmploymentStatusEnum.TEMPORARY,
  ],
  UNEMPLOYED: [EmploymentStatusEnum.RETIRED, EmploymentStatusEnum.NOT_APPLICABLE],
};

export enum TaxType {
  STATE = 'state',
  COUNTY = 'county',
  CITY = 'city',
  INCEPTION = 'inception', // Marketscan seperates taxes for inception fees like protection plans from state/city/county/etc tax
  // These taxes will apply when a customer lives in the county or city configured
  // Contrast this with the regular COUNTY/CITY, which is dealership location based
  CUSTOMER_COUNTY = 'customerCounty',
}

export enum FinanceType {
  PURCHASE = 'purchase',
  LEASE = 'lease',
  CASH = 'cash',
}

// For special cases such as OnePayLease
export enum ExtendedFinanceType {
  ONE_PAY_LEASE = 'onePayLease',
}

// special label for the FinanceCreditForm Ownership Option List
export const FinanceTypeLabels = { ...FinanceType, PURCHASE: 'finance' };

// Default finance terms that the payment builder allows you to select
export const DefaultFinanceTerms = {
  PURCHASE: [48, 60, 72],
  LEASE: [36],
};

export const CreditRatingMax = 850;
export const CreditRatingMin = 1;

export enum PurchaseTermNames {
  TERM1 = 'term1',
  TERM2 = 'term2',
  TERM3 = 'term3',
}

export enum LeaseMileageNames {
  MILEAGE1 = 'mileage1',
  MILEAGE2 = 'mileage2',
  MILEAGE3 = 'mileage3',
  MILEAGE4 = 'mileage4',
  MILEAGE5 = 'mileage5',
}

export enum LeaseTermNames {
  TERM1 = 'term1',
  TERM2 = 'term2',
  TERM3 = 'term3',
  TERM4 = 'term4',
  TERM5 = 'term5',
}

export enum NotifyFormat {
  ADF_BUY = 'adf-buy',
  NEW_LEAD = 'new-lead',
  DRIVER_LICENSE = 'driver-license',
  TURN_OVER = 'turn-over',
  OWNERSHIP_OPTION = 'ownership-option',
  FINANCE_APPLICATION = 'finance-application',
  ONLINE_CONFIRMATION = 'online-confirmation',
  ONLINE_LEAD = 'online-lead',
  ONLINE_SALE = 'online-sale',
  ONLINE_TEST_DRIVE = 'online-test-drive',
  ONLINE_CREDIT_APPLICATION = 'online-credit-application',
  ONLINE_TEST_DRIVE_APPOINTMENT = 'online-test-drive-appointment',
  ONLINE_CREDIT_PULL = 'online-credit-pull',
  DEAL_SUMMARY = 'deal-summary',
  CREDIT_DISCLOSURE = 'credit-disclosure',
  SCANNED_DOCUMENTS = 'scanned-documents',
  OFFER_AGREEMENT = 'offer-agreement',
  PRINT_CREDIT_DISCLOSURE = 'print-credit-disclosure',
  PRINT_CREDIT_APPLICATION = 'print-credit-application',
  PRINT_DRIVER_LICENSE = 'print-driver-license',
  PRINT_SCANNED_DOCUMENTS = 'print-scanned-documents',
  PRINT_PHONEUP_SHEET = 'print-phoneup-sheet',
  NEEDS_ANALYSIS = 'needs-analysis',
  OFFLINE_PRINTER = 'offline-printer',
  TRADE_APPRAISAL = 'trade-appraisal',
  THIRD_PARTY_LEAD = 'third-party-lead',
  THIRD_PARTY_LEAD_NO_VEHICLE = 'third-party-lead-no-vehicle',
  INSURANCE_TEXT = 'insurance-text',
  FINANCING_CONFIRMATION = 'financing-confirmation',
}

export const MileageType = {
  LOW_MILEAGE: 12000,
  STANDARD_MILEAGE: 15000,
  HIGH_MILEAGE: 18000,
};

export enum SourceType {
  INTERNET = 'internet',
  PHONE = 'phone',
  SHOWROOM = 'showroom',
  CAMPAIGN = 'campaign',
}

export enum DealerSocketSourceType {
  Prospect = 'Prospect',
  Customer = 'Customer',
  Employee = 'Employee',
  Vendor = 'Vendor',
  Partner = 'Partner',
}

export enum ReynoldsSourceType {
  'Walk-In' = 'Walk-In',
  Fax = 'Fax',
  Internet = 'Internet',
  Phone = 'Phone',
  List = 'List',
  Other = 'Other',
}

export const VinSolutionsSourceType: Record<string, string> = {
  1: 'Internet',
  2: 'Walk-In',
  3: 'Phone',
  4: 'Import',
};

export const HigherGearSourceType = {
  1: 'Store-Up',
  4: 'Phone-Up',
  5: 'BDC-Up',
  3: 'Internet',
  7: 'Fax-Up',
};

export enum CRMEvent {
  SHOWROOM = 'showroom',
  DEMO = 'demo',
  WRITE_UP = 'write-up',
  TURN_OVER = 'turn-over',
  TRADE_APPRAISAL = 'trade-appraisal',
  SOLD = 'sold',
  NOOP = 'noop',
}

export enum CarStatus {
  IMPORTED = 'imported', // New one, just imported car, ready for building incentives
  AVAILABLE = 'available',
  SOLD = 'sold',
  MANUALLY_ENTERED = 'manuallyEntered',
  SALE_PENDING = 'salePending',
  BYO = 'byo',
}

export const ColorList = [
  'White',
  'Silver',
  'Black',
  'Gray',
  'Blue',
  'Red',
  'Brown',
  'Green',
  'Other',
];

export enum Origin {
  INSTORE = 'In-store',
  ONLINE = 'Online',
  FORWARDED = 'Forwarded',
  DESKING = 'Desking',
  BYO = 'BYO',
}

// max percent difference for an price override compared to car.price
export const PRICE_OVERRIDE_THRESHOLD = 0.25;
// default number of days before a forwarded price override gets expired
export const PRICE_OVERRIDE_FORWARDED_DEFAULT_EXPIRY = 14;

export const RentType = ['rent', 'own', 'family', 'mortgage', 'other'];

export enum RentTypeDropdown {
  rent = 'Rent',
  mortgage = 'Mortgage',
  own = 'Owns Outright',
  family = 'Lives with family',
  other = 'Other',
}

export const CoapplicantRelation = [
  'Resides With',
  'Parent',
  'Spouse',
  'Other',
];

export const VehicleUse = ['Commute', 'Pleasure', 'Business'];
export const Gender = ['M', 'F'];

export const MaritalStatus = [
  'Single',
  'Married',
  'Divorced',
  'Separated',
  'Widowed',
  'Domestic Partner',
];

export const ResidenceType = [
  'Home',
  'Condo',
  'Townhouse',
  'Mobile Home',
  'Other',
];

export const Education = [
  'Some High School',
  'High School',
  'Some College',
  '2 Year College',
  '4 Year College',
  'Graduate Degree',
  'Doctorate',
];

export const LicenseStatus = [
  'Valid',
  'Permit',
  'Expired',
  'Suspended',
  'Cancelled',
  'Not Licensed',
  'Permanently Revoked',
];

export const IncidentType = [
  'Accident',
  'Claim',
  'DUI',
  'Moving Violation',
  'Other Violation',
  'Speeding',
];

export const HasCoapplicant = ['yes', 'no'];

export enum FileFormat {
  PDF = 'pdf',
}

export enum ApplicationType {
  FIVE_LINE = '5 Line',
  CREDIT_APPLICATION = 'Credit Application',
  PHONEUP_SHEET = 'PhoneUp Sheet',
  NEEDS_ANALYSIS = 'Needs Analysis',
  DRIVER_LICENSE = 'Driver License',
  SCANNED_DOCUMENTS = 'Scanned Documents',
  TRADE_APPRAISAL = 'Trade Appraisal',
  DEAL_SUMMARY = 'Deal Summary',
}

export enum ReferenceType {
  FRIEND = 'Friend (over 1 year)',
  RELATIVE = 'Nearest Living Relative',
}

export const DEFAULT_DOWN_PAYMENT = 0.1;
export const DEFAULT_TAX = 0;
export const DEFAULT_APR = 0.069;
export const DEFAULT_PURCHASE_TERM = 72;

export enum OfferCategoryType {
  LOW_INTEREST = 'lowInterest',
  HIGH_REBATE = 'highRebate',
}

export enum DeliveryMethod {
  PICKUP = 'pickup',
  DELIVERY = 'delivery',
}

export enum TestDriveDeliveryMethod {
  INSTORE = 'in-store',
  REMOTE = 'remote',
}

export enum CrmType {
  ELEAD = 'elead',
  DEALERSOCKET = 'dealersocket',
  REYNOLDS = 'reynolds',
  VINSOLUTIONS = 'vinsolutions',
  HIGHERGEAR = 'highergear',
  STANDALONE = 'standalone',
  DGDG_SALESFORCE = 'dgdgsalesforce',
  TEKION = 'tekion',
}

export const CrmTypeName = {
  [CrmType.DEALERSOCKET]: 'DealerSocket',
  [CrmType.ELEAD]: 'Elead',
  [CrmType.HIGHERGEAR]: 'HigherGear',
  [CrmType.REYNOLDS]: 'Reynolds and Reynolds',
  [CrmType.VINSOLUTIONS]: 'VinSolutions',
  [CrmType.STANDALONE]: 'Other',
  [CrmType.DGDG_SALESFORCE]: 'DGDG Salesforce',
  [CrmType.TEKION]: 'Tekion',
};

export enum SaveLoginType {
  ELEAD = 'elead',
  DEALERSOCKET = 'dealersocket',
  REYNOLDS = 'reynolds',
  VINSOLUTIONS = 'vinsolutions',
  HIGHERGEAR = 'highergear',
  STANDALONE = 'standalone',
  VAUTO = 'vauto',
}

export enum FNIType {
  DEALERTRACK = 'dealertrack',
  ROUTEONE = 'routeone',
  CUDL = 'cudl',
}

export const FniTypeName = {
  [FNIType.CUDL]: 'CUDL',
  [FNIType.DEALERTRACK]: 'Dealertrack',
  [FNIType.ROUTEONE]: 'RouteOne',
};

export enum OtherIncomeSources {
  AIDDEPCHILD = 'Aid for Dependent Children',
  CHILDSUPPORT = 'Child Support',
  COLA = 'Cost of Living Allowance (COLA)',
  DISABILITY = 'Disability',
  FSSA = 'Family Subsistence Supplemental Allowance (FSSA)',
  HOUSINGALLOW = 'Housing Allowances',
  MILITARYBAS = 'Military Basic Allowance for Subsistence (BAS)',
  MUNBONDINT = 'Municipal Bond Interest',
  NONTAXINCOME = 'Other Non-Taxable Sources of Income',
  OTHCSTM = 'Other',
  PUBLICASST = 'Public Assistance',
  RAILPENSION = 'Railroad Pension',
  '2NDEMPLOYER' = 'Second Employer',
  SOCSECBEN = 'Social Security Benefits',
  WORKMANCOMP = "Workman's Compensation",
}

export enum PencilStatus {
  REQUESTED = 'requested',
  SENT = 'sent',
  PRESENTED = 'presented',
  ACCEPTED = 'accepted',
  EXPIRED = 'expired',
}

export enum PencilType {
  DEFAULT = 'default',
  MANAGER = 'manager',
}

export enum RequestStatus {
  REQUESTED = 'requested',
  ACCEPTED = 'accepted',
}

export enum DealAudit {
  DRIVER_LICENSE = 'driverLicense',
  SCANNED_DOCUMENTS = 'scannedDocuments',
  CREDIT_APPLICATION = 'creditApplication',
  VEHICLE_SELECTION = 'vehicleSelection',
  TEST_DRIVE = 'testDrive',
  FORM_PRINT = 'formPrint',
  TRADE_IN = 'tradeIn',
  CREATE_CRM_LEAD = 'createCrmLead',
  CREATE_CRM_OPPORTUNITY = 'createCrmOpportunity',
}

export const PointsBreakdown = {
  [DealAudit.DRIVER_LICENSE]: 3,
  [DealAudit.SCANNED_DOCUMENTS]: 3,
  [DealAudit.TEST_DRIVE]: 3,
  [DealAudit.TRADE_IN]: 6,
  [DealAudit.CREDIT_APPLICATION]: 4,
  [DealAudit.VEHICLE_SELECTION]: 3,
  [DealAudit.CREATE_CRM_LEAD]: 2,
  [DealAudit.FORM_PRINT]: 5,
};

export enum PrintingType {
  LOCAL = 'local',
  AIRPRINT = 'airprint',
  GOOGLE_CLOUD = 'google-cloud',
}

export enum TabletForms {
  DEAL_LOOKUP = 'dealLookup',
  CUSTOMER_INFO = 'customerInfo',
  FIVE_LINE = 'fiveLine',
  FULL_CREDIT_APPLICATION = 'fullCreditApplication',
  OWNERSHIP_OPTION = 'ownershipOption',
}

export enum DealResult {
  SOLD = 'sold',
  DID_NOT_PURCHASE = 'did-not-purchase',
}

export enum LeadStatus {
  NEW = 'new',
  EXISTING = 'existing',
  INACTIVE = 'inactive',
  SOLD = 'sold',
}

export enum TradeInType {
  VAUTO = 'vauto',
  FIRSTLOOK = 'firstlook',
}

export enum TradeInSource {
  DEFAULT = 'default',
  KBB = 'KBB',
  ACU_TRADE = 'acuTrade',
  SUBARU_GTP = 'subaruGtp',
  KBBICO = 'KBB_ICO',
  BLACKBOOK = 'BLACKBOOK',
  TRADEPENDING = 'TRADEPENDING',
}

export enum InventoryType {
  OTHER = 'other',
  AUTOJINI = 'autojini',
  AUTOUPLINKTECH = 'autouplinktech',
  DEALERCARSEARCH = 'dealercarsearch',
  DEALERDOTCOM = 'dealerdotcom',
  DEALERINSPIRE = 'dealerinspire',
  DEALERSOCKET = 'dealersocket',
  DEALERTRACK = 'dealertrack',
  DEALERVISION = 'dealervision',
  EBIZAUTOS = 'ebizautos',
  ELEAD = 'elead',
  FUSIONZONE = 'fusionzone',
  HOMENETAUTO = 'homenetauto',
  INVENTORYPLUS = 'inventoryplus',
  MAXDIGITAL = 'maxdigital',
  PIX = 'pix',
  VAUTO = 'vauto',
}

export enum CreditRating {
  POOR = 'poor',
  AVERAGE = 'average',
  GOOD = 'good',
  EXCELLENT = 'excellent',
}

export enum TradeInCondition {
  POOR = 'poor',
  AVERAGE = 'average',
  GOOD = 'good',
  EXCELLENT = 'excellent',
}

export enum COUNTRIES {
  US = 'US',
  CA = 'CA',
}

export enum STATES {
  AL = 'Alabama',
  AK = 'Alaska',
  AS = 'American Samoa',
  AZ = 'Arizona',
  AR = 'Arkansas',
  CA = 'California',
  CO = 'Colorado',
  CT = 'Connecticut',
  DE = 'Delaware',
  DC = 'District Of Columbia',
  FM = 'Federated States Of Micronesia',
  FL = 'Florida',
  GA = 'Georgia',
  GU = 'Guam',
  HI = 'Hawaii',
  ID = 'Idaho',
  IL = 'Illinois',
  IN = 'Indiana',
  IA = 'Iowa',
  KS = 'Kansas',
  KY = 'Kentucky',
  LA = 'Louisiana',
  ME = 'Maine',
  MH = 'Marshall Islands',
  MD = 'Maryland',
  MA = 'Massachusetts',
  MI = 'Michigan',
  MN = 'Minnesota',
  MS = 'Mississippi',
  MO = 'Missouri',
  MT = 'Montana',
  NE = 'Nebraska',
  NV = 'Nevada',
  NH = 'New Hampshire',
  NJ = 'New Jersey',
  NM = 'New Mexico',
  NY = 'New York',
  NC = 'North Carolina',
  ND = 'North Dakota',
  MP = 'Northern Mariana Islands',
  OH = 'Ohio',
  OK = 'Oklahoma',
  OR = 'Oregon',
  PW = 'Palau',
  PA = 'Pennsylvania',
  PR = 'Puerto Rico',
  RI = 'Rhode Island',
  SC = 'South Carolina',
  SD = 'South Dakota',
  TN = 'Tennessee',
  TX = 'Texas',
  UT = 'Utah',
  VT = 'Vermont',
  VI = 'Virgin Islands',
  VA = 'Virginia',
  WA = 'Washington',
  WV = 'West Virginia',
  WI = 'Wisconsin',
  WY = 'Wyoming',
}

export enum STATES_CA {
  AB = 'Alberta',
  BC = 'British Columbia',
  MB = 'Manitoba',
  NB = 'New Brunswick',
  NL = 'Newfoundland and Labrador',
  NS = 'Nova Scotia',
  NT = 'Northwest Territories',
  NU = 'Nunavut',
  ON = 'Ontario',
  PE = 'Prince Edward Island',
  QC = 'Quebec',
  SK = 'Saskatchewan',
  YT = 'Yukon',
}

export enum UserRole {
  ADMIN = 'admin',
  ACCOUNT_MANAGER = 'account_manager',
  DEALER_ADMIN = 'dealer_admin',
  DEALER_MANAGER = 'dealer_manager',
  DESKING_MANAGER = 'desking_manager',
  DIGITAL_FINANCE_SALES_MANAGER = 'digital_finance_sales_manager',
  DIGITAL_FINANCE_FINANCE_MANAGER = 'digital_finance_finance_manager',
  USER = 'user',
  SELF_DESKING = 'self_desking',
  ONLINE_USER = 'online_user',
  INTEGRATION_USER = 'integration_user',
  PRICING_MANAGEMENT = 'pricing_management',
  READ_ONLY_ADMIN = 'read_only_admin',
  LIMITED_DEALER_ADMIN = 'limited_dealer_admin',
  SUPER_ADMIN = 'super_admin'
}

export const UserRoleReadableName: Record<UserRole, string> = {
  [UserRole.ADMIN]: 'Admin',
  [UserRole.ACCOUNT_MANAGER]: 'Account manager',
  [UserRole.DEALER_ADMIN]: 'Dealer admin',
  [UserRole.DEALER_MANAGER]: 'Dealer manager',
  [UserRole.DESKING_MANAGER]: 'Desking manager',
  [UserRole.DIGITAL_FINANCE_SALES_MANAGER]: 'Digital finance sales manager',
  [UserRole.DIGITAL_FINANCE_FINANCE_MANAGER]: 'Digital finance finance manager',
  [UserRole.USER]: 'User',
  [UserRole.SELF_DESKING]: 'Self desking',
  [UserRole.ONLINE_USER]: 'Online user',
  [UserRole.INTEGRATION_USER]: 'Integration user',
  [UserRole.PRICING_MANAGEMENT]: 'Pricing management',
  [UserRole.READ_ONLY_ADMIN]: 'Read Only Admin',
  [UserRole.LIMITED_DEALER_ADMIN]: 'Limited Dealer Admin',
  [UserRole.SUPER_ADMIN]: 'Super Admin'
};

export enum UserType {
  CLIENT = 'client',
  EMPLOYEE = 'employee',
  ADVANCED = 'advanced',
}

export const UserTypeReadableName: Record<UserType, string> = {
  [UserType.CLIENT]: 'Dealer',
  [UserType.ADVANCED]: 'Dealer',
  [UserType.EMPLOYEE]: 'Upstart',
};

export enum Permissions {
  READ = 'read',
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
}

export enum PriceVariableType {
  DISCOUNT = 'discount',
  UPGRADE = 'upgrade',
  OTHER = 'other',
}

export enum OnlineStep {
  browse = 'browse',
  dealStart = 'dealStart',
  tradeIn = 'tradeIn',
  softCreditPull = 'softCreditPull',
  paymentBuilder = 'paymentBuilder',
  upgradePackages = 'upgradePackages',
  creditApplication = 'creditApplication',
  schedulePickup = 'schedulePickup',
  testDrive = 'testDrive',
  deposit = 'deposit',
  vehicleSelection = 'vehicleSelection',
  customerInfo = 'customerInfo',
  accessories = 'accessories',
  insurance = 'insurance',
  documentUpload = 'documentUpload',
  signDocuments = 'signDocuments',
}

export const OnlineStepPath: { [index in OnlineStep]: string } = {
  [OnlineStep.browse]: 'browse',
  [OnlineStep.dealStart]: '',
  [OnlineStep.tradeIn]: 'trade-in',
  [OnlineStep.softCreditPull]: 'credit-pull',
  [OnlineStep.paymentBuilder]: 'payment-builder',
  [OnlineStep.upgradePackages]: 'upgrade-packages',
  [OnlineStep.creditApplication]: 'pre-approval',
  [OnlineStep.schedulePickup]: 'Schedule Pickup/Delivery',
  [OnlineStep.testDrive]: 'schedule-test-drive',
  [OnlineStep.deposit]: 'deposit',
  [OnlineStep.vehicleSelection]: 'v',
  [OnlineStep.customerInfo]: 'user-info',
  [OnlineStep.accessories]: 'accessories',
  [OnlineStep.insurance]: 'insurance',
  [OnlineStep.documentUpload]: 'document-upload',
  [OnlineStep.signDocuments]: 'sign-documents',
};

export const OnlineStepNames: { [index in OnlineStep]: string } = {
  [OnlineStep.browse]: 'Browse',
  [OnlineStep.dealStart]: 'Deal Start',
  [OnlineStep.tradeIn]: 'Trade-in',
  [OnlineStep.softCreditPull]: 'Soft Credit Pull',
  [OnlineStep.paymentBuilder]: 'Payment Builder',
  [OnlineStep.upgradePackages]: 'Protection Plan',
  [OnlineStep.creditApplication]: 'Credit Application',
  [OnlineStep.schedulePickup]: 'Schedule Pickup/Delivery',
  [OnlineStep.testDrive]: 'Test Drive',
  [OnlineStep.deposit]: 'Deposit',
  [OnlineStep.vehicleSelection]: 'Vehicle Selection',
  [OnlineStep.customerInfo]: 'Customer Info',
  [OnlineStep.accessories]: 'Accessories',
  [OnlineStep.insurance]: 'Insurance',
  [OnlineStep.documentUpload]: 'Document Upload',
  [OnlineStep.signDocuments]: 'Sign Documents',
};

export enum WebPage {
  browse = 'browse',
  summary = 'summary',
  examples = 'examples',
  settings = 'settings',
  lending = 'lending',
  offers = 'offers',
}

export enum WebStep {
  payments = 'payments',
  creditApplication = 'application',
  tradeIn = 'trade',
  appointments = 'appointments',
  accessories = 'accessories',
  protectionPlans = 'protection',
  paymentBuilder = 'paymentBuilder',
  documentUpload = 'documentUpload',
  vehicleSelection = 'vehicleSelection',
  dealStart = 'dealStart',
}

export const WebPagePath: { [index in WebPage]: string } = {
  [WebPage.browse]: 'browse',
  [WebPage.summary]: 'summary',
  [WebPage.examples]: 'examples',
  [WebPage.settings]: 'settings',
  [WebPage.lending]: 'lending',
  [WebPage.offers]: 'offers',
};

export const WebStepPath: { [index in WebStep]: string } = {
  [WebStep.payments]: 'payments',
  [WebStep.creditApplication]: 'application',
  [WebStep.tradeIn]: 'trade',
  [WebStep.appointments]: 'appointments',
  [WebStep.accessories]: 'accessories',
  [WebStep.protectionPlans]: 'protection',
  [WebStep.paymentBuilder]: 'payment-builder',
  [WebStep.documentUpload]: 'document-upload',
  [WebStep.vehicleSelection]: 'vehicle-selection',
  [WebStep.dealStart]: 'deal-start',
};

export const WebStepNames: { [index in WebStep]: string } = {
  [WebStep.dealStart]: 'Deal Start',
  [WebStep.tradeIn]: 'Trade-in',
  [WebStep.paymentBuilder]: 'Payment Builder',
  [WebStep.creditApplication]: 'Credit Application',
  [WebStep.vehicleSelection]: 'Vehicle Selection',
  [WebStep.accessories]: 'Accessories',
  [WebStep.documentUpload]: 'Document Upload',
  [WebStep.payments]: 'Payments',
  [WebStep.appointments]: 'Appointments',
  [WebStep.protectionPlans]: 'Protection Plans',
};

export enum StoreStep {
  driverLicense = 'driverLicense',
  documentScan = 'documentScan',
  vehicleSelection = 'vehicleSelection',
  testDrive = 'testDrive',
  tradeIn = 'tradeIn',
  customerInfo = 'customerInfo',
  creditApplication = 'creditApplication',
  printed = 'printed',
  carPaymentBuilder = 'carPaymentBuilder',
  protectionPlans = 'protectionPlans',
  pricingReview = 'pricingReview',
  automaticTestDrive = 'automaticTestDrive',
  accessories = 'accessories',
  insurance = 'insurance',

  financeAndInsuranceProductsViewed = 'financeAndInsuranceProductsViewed',
  tradeStarted = 'tradeStarted',
  incentivesViewed = 'incentivesViewed',
  incentivesSelected = 'incentivesSelected',
  paymentAdjusted = 'paymentAdjusted',
  leaseAdjusted = 'leaseAdjusted',
  financeAdjusted = 'financeAdjusted',
  cashAdjusted = 'cashAdjusted',
  downPaymentAdjusted = 'downPaymentAdjusted',
  termAdjusted = 'termAdjusted',
  financeRateAdjusted = 'financeRateAdjusted',
  creditApplicationsStarted = 'creditApplicationsStarted',
  sendDealToCustomer = 'sendDealToCustomer',
  appointmentViewed = 'appointmentViewed',
  appointmentRequested = 'appointmentRequested',
}

export const InStoreStepNames: Partial<{ [index in StoreStep]: string }> = {
  [StoreStep.customerInfo]: 'Customer Info',
  [StoreStep.driverLicense]: "Driver's License",
  [StoreStep.vehicleSelection]: 'Vehicle Selection',
  [StoreStep.protectionPlans]: 'Protection Plans',
  [StoreStep.tradeIn]: 'Trade In',
  [StoreStep.documentScan]: 'Document Scan',
  [StoreStep.creditApplication]: 'Credit Application',
  [StoreStep.testDrive]: 'Test Drive',
  [StoreStep.accessories]: 'Accessories',
  [StoreStep.carPaymentBuilder]: 'Car Payment Builder',
  [StoreStep.pricingReview]: 'Pricing Review',
};

export const StoreMinutes = {
  [StoreStep.customerInfo]: 5,
  [StoreStep.vehicleSelection]: 15,
  [StoreStep.driverLicense]: 5,
  [StoreStep.documentScan]: 5,
  [StoreStep.carPaymentBuilder]: 15,
  [StoreStep.creditApplication]: 15,
  [StoreStep.printed]: 10,
};

export const OnlineMinutes = {
  [OnlineStep.vehicleSelection]: 30,
  [OnlineStep.tradeIn]: 30,
  [OnlineStep.paymentBuilder]: 15,
  [OnlineStep.creditApplication]: 60,
  [OnlineStep.upgradePackages]: 30,
};

export enum ActionType {
  start = 'start',
  browse = 'browse',
  tradeIn = 'tradeIn',
  paymentOptions = 'paymentBuilder',
  preApproved = 'creditApplication',
  testDrive = 'testDrive',
}

export enum CLIENT_TYPE {
  ONLINE = 'online',
  IN_STORE = 'instore',
}

export enum ONLINE_CLIENT_TYPE {
  ONLINE = 'online',
  WEB = 'web',
}

export const CLIENT_TYPE_HEADER = 'X-ClientType';

export enum Language {
  EN_US = 'en-US',
  EN_CA = 'en-CA',
  ES_MX = 'es-MX',
}

export enum DateFormat {
  MM_DD_YYYY = 'MM/DD/YYYY',
  DD_MM_YYYY = 'DD/MM/YYYY',
  YYYY_MM_DD = 'YYYY/MM/DD',
}

export enum FuelEfficiencyUnit {
  MPG = 'mpg',
  L_100KM = 'l/100km',
}

export enum HOUR_FORMATS {
  HF_24 = '24hr',
  HF_12 = '12hr',
}

export enum CurrencyType {
  USD = 'USD',
  CAD = 'CAD',
}

export enum FeeCategory {
  TAXABLE = 'taxable',
  NON_TAXABLE = 'nontaxable',
  CAPITALIZED_COST = 'capitalizedCost',
  CAPITALIZED_COST_TAXABLE = 'capitalizedCostTaxable',
}

export const FixedValueFee = 'fixedValueFee';

export enum SpecialFees {
  FLORIDA_DOC_STAMP = 'flDocStampFee',
  CALIFORNIA_LICENSE = 'caLicenseFee',
  OREGON_PRIVILEGE_TAX = 'orPrivilegeTax',
  OREGON_CORPORATE_ACTIVITY_TAX = 'orCorporateActivityTax',
  TEXAS_VEHICLE_INVENTORY_TAX = 'txVehicleInventoryTax',
  TENNESSEE_BUSINESS_TAX = 'tnBusinessTax',
  TEXAS_LENDERS_AND_MEMBERS_FEE = 'txLendersAndMembersFee',
}

export enum PriceRuleOffsetType {
  ADD = 'add',
  SUBTRACT = 'subtract',
  ADD_PERCENT = 'add_percent',
  SUBTRACT_PERCENT = 'subtract_percent',
}

export enum PriceRuleSource {
  MSRP = 'msrp',
  INVOICE = 'invoice',
  FEED_PRICE = 'feedPrice',
}

export enum DealerCashRuleType {
  PRICE_OFFSET_FIXED_MAX = 'priceOffsetFixedMax', // a fixed maximum amount
  PRICE_OFFSET_PERCENT = 'priceOffsetPercent',
}

export enum IncentivesProvider {
  AIS = 'ais',
  CHROMEDATA = 'chromedata',
  MARKETSCAN = 'marketscan',
}

export enum CarUpgradeType {
  // manually configured upgrades from dealer.addOns
  DEALER_ADDON = 'dealer_addon',
  // upgrades from shift
  SHIFT_ACCESSORY = 'shift_accessory',
}

/**
 *  Virtual Showroom chat author types
 */
export enum ChatAuthorType {
  // papyrus user
  SALESPERSON = 'salesperson',
  // online user
  CUSTOMER = 'customer',
}

/**
 * Virtual Showroom chat message types
 */
export enum ChatMessageType {
  // a text message
  MESSAGE = 'message',
  // a message for when the customer changes the online page they're viewing
  ONLINE_PAGE_UPDATE = 'online-page-update',
  // a request/accept/reject, by either the customer or salesperson, to start a video chat
  VIDEO_REQUEST = 'video-request',
  VIDEO_ACCEPT = 'video-accept',
  VIDEO_REJECT = 'video-reject',
  // a notification that either the customer or salesperson disconnected from the video chat
  VIDEO_DISCONNECT = 'video-disconnect',
  // a message from the salesperson indicating payments were updated
  PAYMENTS_UPDATE = 'payments-update',
  // a message from the salesperson for requesting a deposit
  DEPOSIT_REQUEST = 'deposit-request',
  // a request from a salesperson to switch a customer's vehicle
  SWITCH_VEHICLE_REQUEST = 'switch-vehicle-request',
  // message indicating the vehicle was accepted
  SWITCH_VEHICLE_ACCEPT = 'switch-vehicle-accept',
  // message indicating the vehicle was rejected
  SWITCH_VEHICLE_REJECT = 'switch-vehicle-reject',
  // message indicating the chat is attempting to reconnect with another salesperson
  CHAT_RECONNECT = 'chat-reconnect',
}

/**
 * Virtual Showroom chat message video reject types
 */
export enum ChatMessageVideoRejectType {
  // online browser doesn't support video chat
  UNSUPPORTED = 'unsupported',
  // user rejected the request
  USER = 'user',
}

export enum ChatStatus {
  REQUESTED = 'requested',
  ACCEPTED = 'accepted',
  REQUEST_TIMEOUT = 'request-timeout',
}

export enum ChatEventName {
  // Video chat is started
  VIDEO_CHAT = 'video-chat',

  // Deposit was requested
  DEPOSIT_REQUEST = 'deposit-request',

  // Chat request was received by salesperson
  CHAT_REQUEST = 'chat-request',

  // Vehicle suggestion was sent
  VEHICLE_SUGGESTION = 'vehicle-suggestion',

  // Salesperson marked himself as available online
  ONLINE_AVAILABILITY = 'online-availability',
}

/**
 * Used to distinguish PEN Provider vs User (custom) created products
 */
export const PenProductSource = {
  PROVIDER: 'provider',
  USER: 'user',
};

// TODO: This should be a catalog on the DB...
export enum Makes {
  'Acura' = 'acura',
  'Alfa-Romeo' = 'alfa-romeo',
  'Aston-Martin' = 'aston-martin',
  'Audi' = 'audi',
  'Bentley' = 'bentley',
  'BMW' = 'bmw',
  'BrightDrop' = 'brightdrop',
  'Buick' = 'buick',
  'Cadillac' = 'cadillac',
  'Canoo' = 'canoo',
  'Chevrolet' = 'chevrolet',
  'Chrysler' = 'chrysler',
  'Corvette' = 'corvette', // This is not a Make, why is it here?
  'Dodge' = 'dodge',
  'Ferrari' = 'ferrari',
  'Fiat' = 'fiat',
  'Fisker' = 'fisker',
  'Ford' = 'ford',
  'Genesis' = 'genesis',
  'GMC' = 'gmc',
  'Honda' = 'honda',
  'Hummer' = 'hummer',
  'Hyundai' = 'hyundai',
  'Ineos' = 'ineos',
  'INFINITI' = 'infiniti',
  'Jaguar' = 'jaguar',
  'Jeep' = 'jeep',
  'Karma' = 'karma',
  'Kia' = 'kia',
  'Lamborghini' = 'lamborghini',
  'Land-Rover' = 'land-rover',
  'Lexus' = 'lexus',
  'Lincoln' = 'lincoln',
  'Lotus' = 'lotus',
  'Lucid' = 'lucid',
  'Maserati' = 'maserati',
  'Mazda' = 'mazda',
  'Mclaren' = 'mclaren',
  'Mercedes-Benz' = 'mercedes-benz',
  'Mercury' = 'mercury',
  'MINI' = 'mini',
  'Mitsubishi' = 'mitsubishi',
  'Mustang' = 'mustang', // This is not a Make, why is it here?
  'Nissan' = 'nissan',
  'Polestar' = 'polestar',
  'Pontiac' = 'pontiac',
  'Porsche' = 'porsche',
  'Ram' = 'ram',
  'Rivian'= 'rivian',
  'Rolls-Royce' = 'rolls-royce',
  'Saturn' = 'saturn',
  'Scion' = 'scion',
  'Smart' = 'smart',
  'Subaru' = 'subaru',
  'Tesla' = 'tesla',
  'Toyota' = 'toyota',
  'Vinfast' = 'vinfast',
  'Volkswagen' = 'volkswagen',
  'Volvo' = 'volvo'
}

export enum ImportStatus {
  IMPORT_STARTED = 'import-started',
  IMPORT_RUNNING = 'import-running',
  IMPORT_FINISHED = 'import-finished',
  IMPORT_FEED_FILE_SKIPPED = 'import-feed-file-skipped',
  IMPORT_TIMEOUT = 'import-timeout',
  IMPORT_ERROR = 'import-error',
}

export enum InsuranceProvider {
  ROOT = 'root',
  COVER = 'cover',
  JERRY = 'jerry',
}

export enum InsuranceEventType {
  TEXT_SENT = 'TEXT_SENT',
  TEXT_CLICK = 'TEXT_CLICK',
  TEXT_CONVERSION = 'TEXT_CONVERSION',
  INSURANCE_APPLICATION = 'INSURANCE_APPLICATION',
}

export enum InsuranceRequestType {
  TEXT = 'text',
}

export enum FinanceProvider {
  ROUTEONE = 'routeone',
  WESTLAKE = 'westlake',
  DEALERTRACK = 'dealertrack',
  UPSTART = 'upstart',
}

export enum FinanceApplicationStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  FAILED = 'failed',
}

export enum FinanceResponseStatus {
  APPROVED = 'approved',
  DENIED = 'denied',
  CONDITION = 'condition',
  COUNTERED = 'countered',
  PRELIMINARY = 'preliminary',
  WITHDRAWN = 'withdrawn',
  BOOKED = 'booked',
  ERROR = 'error',
  NONE = 'none',
}

export enum Signer {
  APPLICANT = 'applicant',
  COAPPLICANT = 'coapplicant',
  MANAGER = 'manager',
}

export enum FormPackType {
  ONLINE = 'Online',
  INSTORE = 'In-store',
}

export enum LendingFlowType {
  AUTOMATIC = 'automatic',
  MANAGER = 'manager',
}

export enum AppointmentType {
  TEST_DRIVE = 'testDrive',
  PICKUP_OR_DELIVERY = 'pickupOrDelivery',
}

export default Constants;

export enum PixallAnalyticsEventTypes {
  DR_TOOL_OPEN = 'DR_TOOL_OPENED',
  DR_CHAT_START = 'DR_TOOL_OPENED_CHAT_INITIATED',
  DR_UNLOCK_FORM_SUBMITTED = 'FORM_UNLOCK_SUBMISSION',
  DR_INITIAL_INTERACTION = 'DR_INITIAL_INTERACTION',
  DR_PAYMENT_CALCULATOR_ENGAGEMENT = 'PENCILING_PAYMENT_CALCULATOR',
  DR_TRADEIN_START = 'DR_TRADE_START',
  DR_TRADEIN_SUBMISSION = 'DR_TRADEIN_SUBMISSION',
  DR_CREDIT_APP_START = 'CREDIT_APP_STARTED',
  DR_CREDIT_APP_SUBMIT = 'DR_SUBMISSION',
  DR_DEPOSIT_SUBMIT = 'DEPOSIT_SUBMITTED',
  DR_DEAL_SUBMIT = 'DEAL_SUBMITTED',
  DR_DIGITAL_CONTRACT_START = 'DR_DIGITAL_CONTRACT_START',
  DR_DIGITAL_CONTRACT_SUBMIT = 'DIGITAL_CONTRACT_SUBMITTED',
  DR_DELIVERY_REQUEST = 'HOME_DELIVERY_REQUEST',
}

export enum ShiftAnalyticsEventTypes {
  drInitialClick = 'drInitialClick',
  drPaymentCalcFinish = 'drPaymentCalcFinish',
  drLeadFormStart = 'drLeadFormStart',
  drLeadFormFinish = 'drLeadFormFinish',
  drFiFinish = 'drFiFinish',
  drDropSave = 'drDropSave',

  // v2.0 has those new events below
  drImpression = 'drImpression',
  drTradeInShown = 'drTradeInShown',
  drTradeInStart = 'drTradeInStart',
  drTradeInFinish = 'drTradeInFinish',
  drPaymentCalcShown = 'drPaymentCalcShown',
  drPaymentCalcInteraction = 'drPaymentCalcInteraction',
  drLeadFormShown = 'drLeadFormShown',
  drApptSchedShown = 'drApptSchedShown',
  drApptSchedStart = 'drApptSchedStart',
  drApptSchedFinish = 'drApptSchedFinish',
  drPreQShown = 'drPreQShown',
  drPreQStart = 'drPreQStart',
  drPreQFinish = 'drPreQFinish',
  drCreditAppShown = 'drCreditAppShown',
  drCreditAppStart = 'drCreditAppStart',
  drCreditAppFinish = 'drCreditAppFinish',
  drFiShown = 'drFiShown',
  drAccessoriesShown = 'drAccessoriesShown',
  drAddToCart = 'drAddToCart',
  drAccessoriesFinish = 'drAccessoriesFinish',
  drContractShown = 'drContractShown',
  drContractStart = 'drContractStart',
  drContractFinish = 'drContractFinish',
  drOfferAdded = 'drOfferAdded',

  // byo events
  byoBuildStart = 'byoBuildStart',
  byoModelShown = 'byoModelShown',
  byoModelSelected = 'byoModelSelected',
  byoModelTrimShown = 'byoModelTrimShown',
  byoModelTrimSelected = 'byoModelTrimSelected',
  byoInteraction = 'byoInteraction',
  byoExteriorColorShown = 'byoExteriorColorShown',
  byoExteriorColorSelected = 'byoExteriorColorSelected',
  byoInteriorColorShown = 'byoInteriorColorShown',
  byoInteriorColorSelected = 'byoInteriorColorSelected',
  byoPackagesShown = 'byoPackagesShown',
  byoPackagesSelected = 'byoPackagesSelected',
  byoLeadFormShown = 'byoLeadFormShown',
  byoLeadFormStart = 'byoLeadFormStart',
  byoLeadFormFinish = 'byoLeadFormFinish',
  byoDropSave = 'byoDropSave',
  byoSummaryShown = 'byoSummaryShown',
  byoPurchaseStart = 'byoPurchaseStart',
  byoAccessoriesShown = 'byoAccessoriesShown',
  byoAccessoriesSelected = 'byoAccessoriesSelected',
  byoTransmissionShown = 'byoTransmissionShown',
  byoTransmissionSelected = 'byoTransmissionSelected',

  // kia ev9 reservation event
  reservationTrimShown = 'reservationTrimShown',
  reservationTrimSelected = 'reservationTrimSelected',
  reservationColorsShown = 'reservationColorsShown',
  reservationColorSelected = 'reservationColorSelected',
  reservationExteriorColorsShown = 'reservationExteriorColorsShown',
  reservationExteriorColorSelected = 'reservationExteriorColorSelected',
  reservationInteriorColorsShown = 'reservationInteriorColorsShown',
  reservationInteriorColorSelected = 'reservationInteriorColorSelected',
  reservationOptionsShown = 'reservationOptionsShown',
  reservationsOptionsSelected = 'reservationsOptionsSelected',
  reservationLeadFormShown = 'reservationLeadFormShown',
  reservationLeadFormStart = 'reservationLeadFormStart',
  reservationLeadFormSubmit = 'reservationLeadFormSubmit',
  reservationSummaryShown = 'reservationSummaryShown',
}

// When tradeIn info is actually pulled from those systems, they come with
// extra info that we don't use internally. Depending on the source chosen here,
// the tradeInMeta takes a different shape
export enum TradeInDataSource {
  VINSOLUTIONS = 'vinsolutions',
  ELEAD = 'elead',
}

export const COMMUNITY_PROPERTY_STATES = [
  'AZ',
  'CA',
  'ID',
  'LA',
  'NV',
  'NM',
  'TX',
  'WA',
  'WI',
];

export enum STICKER_FORMAT {
  JPG = 'jpg',
  PDF = 'pdf',
}

// Used for Upstart applications
export enum IncomeType {
  EMPLOYED_SALARY = 'Employed - Salary',
  EMPLOYED_HOURLY = 'Employed - Hourly',
  EMPLOYED_INDEPENDENT_CONTRACTOR = 'Employed - Independent Contractor',
  SELF_EMPLOYED_SOLE_PROPRIETOR = 'Self Employed - Sole Proprietor',
  SELF_EMPLOYED_PARTNERSHIP_LLC = 'Self Employed - Partnership/LLC',
  OTHER = 'Other',
}

// Used for "Employed - Hourly" Income Type for Upstart applications
export enum IncomeSubType {
  FULL_TIME = 'Full Time',
  PART_TIME = 'Part Time',
  CONTRACT = 'Contract',
}

// Used for "Employed - Hourly" Income Type for Upstart applications
export enum PaycheckFrequency {
  WEEKLY = 'Weekly',
  BIWEEKLY = 'Biweekly',
  SEMIMONTHLY = 'Semimonthly',
  MONTHLY = 'Monthly',
}

export enum OtherIncomeType {
  ALIMONY = 'Alimony',
  CHILD_SUPPORT = 'Child Support',
  RETIREMENT_AND_PENSION_INCOME = 'Retirement and pension income',
  RENTAL_INCOME = 'Rental Income',
  DISABILITY_BENEFITS = 'Disability benefits',
  STIPEND_OR_SCHOLARSHIP = 'Stipend or Scholarship',
  OTHER = 'Other',
}

export enum NotificationChannel {
  email = 'email',
}

export enum BlackbookValuationType {
  VIN = 'VIN',
  YMMSS = 'YMMSS',
}

export enum DealerType {
  Default = 'Default',
  BYO_ENABLED = 'BYOEnabled',
  BYO_ONLY = 'BYOOnly',
}

export enum IncomeIntervalType {
  ANNUAL = 'annual',
  MONTHLY = 'monthly',
}

export const NO_SELECTED_MAKES = 'NO_SELECTED_MAKES';

export const FAUX_TRADE_IN_ID = 'faux-trade-in';

export enum LenderNames {
  ALLY = 'ally',
  US_BANK = 'us_bank',
  CHASE = 'chase',
  BANK_OF_AMERICA = 'bank_of_america',
  SANTANDER = 'santander',
}

export const LENDERS = [
  'Ally Financial',
  'US Bank',
  'Chase',
  'Bank of America',
  'Santander Consumer USA, Inc.',
];

export const defaultLendersIDs = [1, 5, 17, 37, 457];

export const LENDER_KEY_TO_LABEL = {
  [LenderNames.ALLY]: 'Ally Financial',
  [LenderNames.US_BANK]: 'US Bank',
  [LenderNames.CHASE]: 'Chase',
  [LenderNames.BANK_OF_AMERICA]: 'Bank of America',
  [LenderNames.SANTANDER]: 'Santander Consumer USA, Inc.',
};

export const LENDER_KEY_TO_ID = {
  [LenderNames.ALLY]: 1,
  [LenderNames.US_BANK]: 5,
  [LenderNames.CHASE]: 17,
  [LenderNames.BANK_OF_AMERICA]: 37,
  [LenderNames.SANTANDER]: 457,
};

export const ORDERED_LENDERS: LenderNames[] = [
  LenderNames.ALLY,
  LenderNames.US_BANK,
  LenderNames.CHASE,
  LenderNames.BANK_OF_AMERICA,
  LenderNames.SANTANDER,
];
export enum ProtectionPlansSelectionType {
  Mandatory = 'mandatory',
  Default = 'default',
  None = 'none',
  Disabled = 'disabled',
}

export enum PreferredContactMethod {
  EMAIL = 'email',
  PHONE = 'phone',
  TEXT = 'text',
}

export enum ThirdPartyAccessoryProvider {
  ShiftDigitalFTP = 'shiftDigitalFTP',
  Insignia = 'insignia',
}

export enum DisplayFormat {
  MONEY = 'money',
  PERCENT = 'percent',
}

export const UPSTART_MIN_FINANCE_AMOUNT = 9_000;
export const UPSTART_MAX_FINANCE_AMOUNT = 100_000;

export const MINIMUM_LTV_FOR_GAP: { [key in UpstartLendingStates]?: number } = {
  CA: 0.8,
  IN: 0.8,
};

/* The max GAP premium percentage based on potential changes to GAP price.
  We derive the values from the equation (otherChargeNotIncludingGap + maxGapValue) * maxGapPremiumPercentage = maxGapValue.

  For example, the max GAP premium limit for CA is technically .04, but gap price also impacts the finance amount.
  We derive the maxGapValue from the equation (otherChargeNotIncludingGap + maxGapValue) * .04 = maxGapValue.
  which simplifies to .0416 * otherChargeNotIncludingGap = maxGapValue
 */

export type GapCalculationRule = {
  type: 'percentage' | 'fixed' | 'hybrid';
  percentage?: number;
  fixedAmount?: number;
  description: string;
};

export const STATE_GAP_CALCULATION_RULES: {
  [key in UpstartLendingStates]?: GapCalculationRule;
} = {
  CO: {
    type: 'hybrid',
    percentage: 0.0416,
    fixedAmount: 600,
    description: 'Cannot exceed 4% of amount financed (excluding GAP) or $600, whichever is greater'
  },
  CA: {
    type: 'percentage',
    percentage: 0.0416,
    description: 'Maximum GAP premium percentage of amount financed (excluding GAP)'
  }
};
